import { Alert, Box, Paper, Typography } from "@mui/material";
import { BaseTable } from "components";
import * as sort from "enums/sort";
import { UseHasPermission, UseMaterialColors, UseTheme } from "lib/hooks";
import { UsePlantUserAccessManagersContactInfo } from "lib/hooks/usePlantUserAccessManagersContactInfo";
import { UseSectorAdminContactInfo } from "lib/hooks/useSectorAdminContactInfo";
import { GradientHeaderText } from "lib/styles/header";
import { TableHeaderStyles } from "lib/styles/table";
import {
	PlantUserAccessManagerContact,
	SectorAdminContact,
} from "models/dto/user";
import { useCallback, useMemo, useState } from "react";

import {
	CreateHeadCells,
	CreatePlantUserHeadCells,
} from "./accessContactInfo/accessContactInfoHeadCells";
import {
	ContactInfoSectionHeaderSx,
	ContactInfoSectionPaperSx,
	ContactInfoSectionTitleSx,
} from "./accessContactInfo/accessContactInfoStyles";
import { PlantUserAccessManagersTable } from "./accessContactInfo/PlantUserAccessManagersTable";
import { SectorAdminTable } from "./accessContactInfo/SectorAdminTable";

export type SectorAdminContactTableSortState = {
	order: "asc" | "desc";
	orderBy: keyof SectorAdminContact;
};

/**
 * Sector Admin "Access contact information": sector administrators and plant user access managers in
 * separate sections (each with its own API hook, error handling, and tables under `accessContactInfo/`).
 */
export function MCCAccessContactInfoPage() {
	const materialColors = UseMaterialColors();
	const { theme } = UseTheme();
	const { isPlantUsersAccessManager } = UseHasPermission();
	const {
		contactInfo: plantUserContactInfo,
		loading: plantUserLoading,
		error: plantUserError,
	} = UsePlantUserAccessManagersContactInfo();
	const { contactInfo, loading, error } = UseSectorAdminContactInfo();

	const [sortStates, setSortStates] = useState<
		Map<string, SectorAdminContactTableSortState>
	>(new Map());

	const getSortState = useCallback(
		(sectorId: string): SectorAdminContactTableSortState => {
			const state = sortStates.get(sectorId);
			return state || { order: "asc", orderBy: "name" };
		},
		[sortStates]
	);

	const handleRequestSort = useCallback(
		(sectorId: string, property: keyof SectorAdminContact) => {
			setSortStates((prev) => {
				const newMap = new Map(prev);
				const currentState = newMap.get(sectorId) || {
					order: "asc",
					orderBy: "name",
				};
				const isAsc =
					currentState.orderBy === property &&
					currentState.order === "asc";
				newMap.set(sectorId, {
					order: isAsc ? "desc" : "asc",
					orderBy: property,
				});
				return newMap;
			});
		},
		[]
	);

	const sortAdmins = useCallback(
		(admins: SectorAdminContact[], sectorId: string): SectorAdminContact[] => {
			const sortState = getSortState(sectorId);
			return [...admins].sort((a, b) => {
				const getValue = (
					admin: SectorAdminContact,
					key: keyof SectorAdminContact
				): string => {
					const val = admin[key];
					if (val === null || val === undefined) return "";
					return String(val).toLowerCase();
				};

				const aValue = getValue(a, sortState.orderBy);
				const bValue = getValue(b, sortState.orderBy);
				const cmp = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
				return sortState.order === "asc" ? cmp : -cmp;
			});
		},
		[getSortState]
	);

	const getSortEnum = useCallback(
		(sectorId: string) => {
			const sortState = getSortState(sectorId);
			const sortField = sortState.orderBy as string;
			return sortState.order === "asc"
				? sort.Asc(sortField)
				: sort.Desc(sortField);
		},
		[getSortState]
	);

	const skeletonHeadCells = useMemo(
		() => CreateHeadCells(materialColors),
		[materialColors]
	);

	return (
		<Box p={3}>
			<Box mb={3}>
				<Typography variant="h5" sx={GradientHeaderText(theme, false)}>
					MCC Access Contact Information
				</Typography>
			</Box>

			<Paper elevation={0} sx={{ mb: 4, ...ContactInfoSectionPaperSx(theme) }}>
				<Box sx={ContactInfoSectionHeaderSx(theme)}>
					<Typography component="h2" sx={ContactInfoSectionTitleSx(theme)}>
						Plant User Access Managers
					</Typography>
				</Box>
				<Box sx={{ p: 2 }}>
					<Typography
						variant="body2"
						sx={{
							color: materialColors.onSurfaceVariant,
							mb: 2,
						}}
					>
						To be added to a plant, contact a Plant User Access
						Manager for your sector and plant in the list below.
					</Typography>
					{plantUserError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{plantUserError}
						</Alert>
					)}
					{(plantUserLoading || plantUserContactInfo.length > 0) && (
						<>
							{plantUserLoading && plantUserContactInfo.length === 0 ? (
								<BaseTable<
									PlantUserAccessManagerContact,
									keyof PlantUserAccessManagerContact
								>
									data={[]}
									headCells={CreatePlantUserHeadCells(materialColors)}
									config={{
										stickyHeader: true,
										header: {
											title: (
												<Typography
													variant="h5"
													sx={GradientHeaderText(
														theme,
														false
													)}
												>
													Loading…
												</Typography>
											),
										},
									}}
									loading={true}
									hideActions={true}
									getRowId={() => ""}
									sx={TableHeaderStyles(theme)}
								/>
							) : (
								plantUserContactInfo.map(
									(sectorData, index) => (
										<Box
											key={sectorData.sectorId}
											mb={
												index <
												plantUserContactInfo.length - 1
													? 4
													: 0
											}
										>
											<PlantUserAccessManagersTable
												sectorData={sectorData}
												loading={plantUserLoading}
												materialColors={materialColors}
												theme={theme}
											/>
										</Box>
									)
								)
							)}
						</>
					)}
					{!plantUserLoading && plantUserContactInfo.length === 0 && (
						<Box
							sx={{
								p: 3,
								textAlign: "center",
								backgroundColor: materialColors.surface[1],
								borderRadius: 1,
							}}
						>
							<Typography
								variant="body1"
								sx={{ color: materialColors.onSurfaceVariant }}
							>
								No Plant User Access Managers found.
							</Typography>
						</Box>
					)}
				</Box>
			</Paper>

			{isPlantUsersAccessManager && (
				<Paper elevation={0} sx={ContactInfoSectionPaperSx(theme)}>
					<Box sx={ContactInfoSectionHeaderSx(theme)}>
						<Typography component="h2" sx={ContactInfoSectionTitleSx(theme)}>
							Sector Administrators
						</Typography>
					</Box>
					<Box sx={{ p: 2 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						{(loading || contactInfo.length > 0) && (
							<>
								{loading && contactInfo.length === 0 ? (
									<BaseTable<SectorAdminContact, keyof SectorAdminContact>
										data={[]}
										headCells={skeletonHeadCells}
										config={{
											stickyHeader: true,
											header: {
												title: (
													<Typography
														variant="h5"
														sx={GradientHeaderText(
															theme,
															false
														)}
													>
														Loading…
													</Typography>
												),
											},
										}}
										loading={true}
										hideActions={true}
										getRowId={() => ""}
										sx={TableHeaderStyles(theme)}
									/>
								) : (
									contactInfo.map((sectorData, index) => (
										<Box
											key={sectorData.sectorId}
											mb={index < contactInfo.length - 1 ? 4 : 0}
										>
											<SectorAdminTable
												sectorData={sectorData}
												loading={loading}
												materialColors={materialColors}
												theme={theme}
												sortAdmins={sortAdmins}
												handleRequestSort={handleRequestSort}
												getSortEnum={getSortEnum}
											/>
										</Box>
									))
								)}
							</>
						)}
						{!loading && contactInfo.length === 0 && (
							<Box
								sx={{
									p: 4,
									textAlign: "center",
									backgroundColor: materialColors.surface[1],
									borderRadius: 1,
								}}
							>
								<Typography
									variant="body1"
									sx={{ color: materialColors.onSurfaceVariant }}
								>
									No sector administrators found.
								</Typography>
							</Box>
						)}
					</Box>
				</Paper>
			)}
		</Box>
	);
}

export { PlantUserAccessManagersTable } from "./accessContactInfo/PlantUserAccessManagersTable";
export type { PlantUserAccessManagersTableSortState } from "./accessContactInfo/PlantUserAccessManagersTable";
export { SectorAdminTable } from "./accessContactInfo/SectorAdminTable";
export type { SectorAdminTableProps } from "./accessContactInfo/SectorAdminTable";

